import type {
  Annotation,
  AnnotationComment,
  AnnotationId,
  Attachment,
  AttachmentId,
  Call,
  CallId,
  ActivityEvent,
  Channel,
  ChannelId,
  ChannelMember,
  ChecklistItem,
  Comment,
  CommentId,
  EpochMs,
  Folder,
  FolderId,
  InboxItem,
  InboxItemId,
  InboxState,
  KnowledgeDocId,
  Label,
  LabelId,
  Message,
  MessageId,
  Milestone,
  MilestoneId,
  MilestoneState,
  MockCache,
  ModelAnnotation,
  Presence,
  Project,
  ProjectId,
  Reaction,
  SubscriptionState,
  SubscriptionTarget,
  SubscriptionTargetKind,
  Task,
  TaskId,
  TaskKey,
  TaskLink,
  TaskStatus,
  TaskType,
  TaskTypeId,
  Thread,
  TimeEntry,
  User,
  UserId,
  Watcher,
  Workspace,
  WorkspaceId,
} from './types';
import { computeProgress, milestoneState, rollupEstimateMinutes, watchersOf } from './helpers';
import { isPersonalAttachment } from './storage-folders/system';

export function selectMe(cache: MockCache): UserId {
  return cache.me;
}

export function selectUser(cache: MockCache, id: UserId): User | undefined {
  return cache.users.get(id);
}

export function selectPresence(cache: MockCache, userId: UserId): Presence | undefined {
  return cache.presence.get(userId);
}

export function selectWorkspace(cache: MockCache, id: WorkspaceId): Workspace | undefined {
  return cache.workspaces.get(id);
}

export function selectFolder(cache: MockCache, id: FolderId): Folder | undefined {
  return cache.folders.get(id);
}

export function selectProject(cache: MockCache, id: ProjectId): Project | undefined {
  return cache.projects.get(id);
}

export function selectMilestone(cache: MockCache, id: MilestoneId): Milestone | undefined {
  return cache.milestones.get(id);
}

export function selectMilestoneState(cache: MockCache, id: MilestoneId): MilestoneState | undefined {
  const milestone = cache.milestones.get(id);
  if (milestone === undefined) {
    return undefined;
  }
  return milestoneState(milestone, cache.nowMs());
}

export function selectLabel(cache: MockCache, id: LabelId): Label | undefined {
  return cache.labels.get(id);
}

export function selectTaskType(cache: MockCache, id: TaskTypeId): TaskType | undefined {
  return cache.taskTypes.get(id);
}

export function selectTask(cache: MockCache, id: TaskId): Task | undefined {
  return cache.tasks.get(id);
}

export function selectTaskByKey(cache: MockCache, key: TaskKey): Task | undefined {
  const id = cache.index.taskByKey.get(key);
  if (id === undefined) {
    return undefined;
  }
  return cache.tasks.get(id);
}

export function selectTasksByProject(cache: MockCache, projectId: ProjectId): Task[] {
  const tasks: Task[] = [];
  for (const id of cache.index.tasksByProject.get(projectId) ?? []) {
    const task = cache.tasks.get(id);
    if (task !== undefined) {
      tasks.push(task);
    }
  }
  return tasks;
}

export function selectTasksByStatus(cache: MockCache, status: TaskStatus): Task[] {
  const tasks: Task[] = [];
  for (const id of cache.index.tasksByStatus.get(status) ?? []) {
    const task = cache.tasks.get(id);
    if (task !== undefined) {
      tasks.push(task);
    }
  }
  return tasks;
}

export function selectSubtasks(cache: MockCache, parentId: TaskId): Task[] {
  const tasks: Task[] = [];
  for (const id of cache.index.subtasksByParent.get(parentId) ?? []) {
    const task = cache.tasks.get(id);
    if (task !== undefined) {
      tasks.push(task);
    }
  }
  return tasks;
}

export function selectEstimateRollup(cache: MockCache, taskId: TaskId): number | null {
  const task = cache.tasks.get(taskId);
  if (task === undefined) {
    return null;
  }
  const children: (number | null)[] = [];
  for (const id of cache.index.subtasksByParent.get(taskId) ?? []) {
    const child = cache.tasks.get(id);
    if (child !== undefined && child.deletedAt === null) {
      children.push(child.estimateMinutes);
    }
  }
  return rollupEstimateMinutes(task.estimateMinutes, children);
}

export function selectTaskProgress(cache: MockCache, taskId: TaskId): { done: number; total: number } {
  const task = cache.tasks.get(taskId);
  if (task === undefined) {
    return { done: 0, total: 0 };
  }
  return computeProgress(task);
}

export function selectWatchers(cache: MockCache, target: SubscriptionTarget): Watcher[] {
  return watchersOf(cache, target);
}

export function selectSubscriptionState(
  cache: MockCache,
  userId: UserId,
  targetKind: SubscriptionTargetKind,
  targetId: string,
): SubscriptionState | null {
  return cache.subscriptions.get(`${String(userId)}:${targetKind}:${targetId}`)?.state ?? null;
}

export function selectChecklistByTask(cache: MockCache, taskId: TaskId): ChecklistItem[] {
  const items: ChecklistItem[] = [];
  for (const id of cache.index.checklistByTask.get(taskId) ?? []) {
    const item = cache.checklist.get(id);
    if (item !== undefined && item.deletedAt === null) {
      items.push(item);
    }
  }
  items.sort((a, b) => (a.sortKey < b.sortKey ? -1 : 1));
  return items;
}

export function selectChecklistProgress(cache: MockCache, taskId: TaskId): { done: number; total: number } {
  const items = selectChecklistByTask(cache, taskId);
  return { done: items.filter((item) => item.done).length, total: items.length };
}

export function selectCommentsByTask(cache: MockCache, taskId: TaskId): Comment[] {
  const comments: Comment[] = [];
  for (const id of cache.index.commentsByTask.get(taskId) ?? []) {
    const comment = cache.comments.get(id);
    if (comment !== undefined && comment.deletedAt === null) {
      comments.push(comment);
    }
  }
  comments.sort((a, b) => Number(a.createdAt) - Number(b.createdAt));
  return comments;
}

export function selectComment(cache: MockCache, id: CommentId): Comment | undefined {
  return cache.comments.get(id);
}

export interface TaskLinksGrouped {
  blocks: TaskLink[];
  blockedBy: TaskLink[];
  related: TaskLink[];
}

export function selectLinksByTask(cache: MockCache, taskId: TaskId): TaskLinksGrouped {
  const grouped: TaskLinksGrouped = { blocks: [], blockedBy: [], related: [] };
  for (const id of cache.index.linksByTask.get(taskId) ?? []) {
    const link = cache.links.get(id);
    if (link === undefined) {
      continue;
    }
    if (link.kind === 'relates') {
      grouped.related.push(link);
    } else if (link.fromTaskId === taskId) {
      grouped.blocks.push(link);
    } else {
      grouped.blockedBy.push(link);
    }
  }
  return grouped;
}

export function selectTimeByTask(cache: MockCache, taskId: TaskId): TimeEntry[] {
  const entries: TimeEntry[] = [];
  for (const id of cache.index.timeByTask.get(taskId) ?? []) {
    const entry = cache.timeEntries.get(id);
    if (entry !== undefined) {
      entries.push(entry);
    }
  }
  entries.sort((a, b) => (a.spentOn < b.spentOn ? -1 : 1));
  return entries;
}

export function selectTimeSpentTotal(cache: MockCache, taskId: TaskId): number {
  let total = 0;
  for (const entry of selectTimeByTask(cache, taskId)) {
    total += entry.minutes;
  }
  return total;
}

export function selectActivityByTask(cache: MockCache, taskId: TaskId): ActivityEvent[] {
  const events: ActivityEvent[] = [];
  for (const id of cache.index.activityByTask.get(taskId) ?? []) {
    const found = cache.activity.find((event) => event.id === id);
    if (found !== undefined) {
      events.push(found);
    }
  }
  events.sort((a, b) => a.seq - b.seq);
  return events;
}

export function selectChannel(cache: MockCache, id: ChannelId): Channel | undefined {
  return cache.channels.get(id);
}

export function selectChannelMembers(cache: MockCache, channelId: ChannelId): ChannelMember[] {
  const members: ChannelMember[] = [];
  for (const member of cache.members.values()) {
    if (member.channelId === channelId) {
      members.push(member);
    }
  }
  return members;
}

export function selectChannelMember(
  cache: MockCache,
  channelId: ChannelId,
  userId: UserId,
): ChannelMember | undefined {
  return cache.members.get(`${String(channelId)}:${String(userId)}`);
}

export function selectChannelMessages(
  cache: MockCache,
  channelId: ChannelId,
  viewerId?: UserId,
): Message[] {
  const viewer = viewerId ?? cache.me;
  const now = Number(cache.nowMs());
  const messages: Message[] = [];
  for (const id of cache.index.messagesByChannel.get(channelId) ?? []) {
    const message = cache.messages.get(id);
    if (message === undefined || message.threadRootId !== null) {
      continue;
    }
    if (!message.published && message.authorId !== viewer) {
      continue;
    }
    if (message.sendAt !== null && Number(message.sendAt) > now && message.authorId !== viewer) {
      continue;
    }
    messages.push(message);
  }
  return messages;
}

export function selectMessage(cache: MockCache, id: MessageId): Message | undefined {
  return cache.messages.get(id);
}

export function selectThread(cache: MockCache, rootMessageId: MessageId): Thread | undefined {
  return cache.threads.get(rootMessageId);
}

export function selectThreadReplies(cache: MockCache, rootMessageId: MessageId): Message[] {
  const replies: Message[] = [];
  for (const id of cache.index.repliesByThread.get(rootMessageId) ?? []) {
    const message = cache.messages.get(id);
    if (message !== undefined) {
      replies.push(message);
    }
  }
  return replies;
}

export function selectReactions(
  cache: MockCache,
  ownerKind: 'message' | 'comment',
  ownerId: MessageId | CommentId,
): Reaction[] {
  const reactions: Reaction[] = [];
  for (const id of cache.index.reactionsByOwner.get(`${ownerKind}:${String(ownerId)}`) ?? []) {
    const reaction = cache.reactions.get(id);
    if (reaction !== undefined) {
      reactions.push(reaction);
    }
  }
  return reactions;
}

function isForeignPersonal(cache: MockCache, attachment: Attachment): boolean {
  return isPersonalAttachment(attachment) && attachment.uploaderId !== cache.me;
}

export function selectAttachments(
  cache: MockCache,
  ownerKind: Attachment['ownerKind'],
  ownerId: Attachment['ownerId'],
): Attachment[] {
  const attachments: Attachment[] = [];
  for (const attachment of cache.attachments.values()) {
    if (attachment.ownerKind === ownerKind && attachment.ownerId === ownerId && !isForeignPersonal(cache, attachment)) {
      attachments.push(attachment);
    }
  }
  return attachments.sort((a, b) => Number(a.createdAt) - Number(b.createdAt) || String(a.id).localeCompare(String(b.id)));
}

export function canUserRemoveAttachment(cache: MockCache, userId: UserId, attachment: Attachment): boolean {
  if (isPersonalAttachment(attachment)) return attachment.uploaderId === userId;
  if (attachment.ownerKind === 'task' || attachment.ownerKind === 'doc') return true;
  if (cache.users.get(userId)?.role === 'admin') return true;
  if (attachment.ownerKind === 'message') {
    const message = cache.messages.get(attachment.ownerId as MessageId);
    return (message === undefined ? attachment.uploaderId : message.authorId) === userId;
  }
  const comment = cache.comments.get(attachment.ownerId as CommentId);
  return (comment?.authorId ?? attachment.uploaderId) === userId;
}

export function canRemoveAttachment(cache: MockCache, attachment: Attachment): boolean {
  return canUserRemoveAttachment(cache, cache.me, attachment);
}

type AttachmentOwnerState = 'alive' | 'gone' | 'draft';

function isLiveTask(cache: MockCache, taskId: TaskId): boolean {
  return cache.tasks.get(taskId)?.deletedAt === null;
}

function attachmentOwnerState(cache: MockCache, attachment: Attachment): AttachmentOwnerState {
  if (attachment.ownerKind === 'storage') return 'alive';
  if (attachment.ownerKind === 'message') {
    const message = cache.messages.get(attachment.ownerId as MessageId);
    if (message === undefined) return 'draft';
    return message.deletedAt === null ? 'alive' : 'gone';
  }
  if (attachment.ownerKind === 'comment') {
    const comment = cache.comments.get(attachment.ownerId as CommentId);
    if (comment === undefined) return 'draft';
    return comment.deletedAt === null && isLiveTask(cache, comment.taskId) ? 'alive' : 'gone';
  }
  if (attachment.ownerKind === 'doc') {
    return cache.docs.has(attachment.ownerId as KnowledgeDocId) ? 'alive' : 'gone';
  }
  return isLiveTask(cache, attachment.ownerId as TaskId) ? 'alive' : 'gone';
}

function canSeeMessageChannel(cache: MockCache, userId: UserId, messageId: MessageId): boolean {
  const message = cache.messages.get(messageId);
  const channel = message === undefined ? undefined : cache.channels.get(message.channelId);
  if (message === undefined || channel === undefined) return false;
  return (channel.kind === 'public' && !channel.closed) || cache.members.has(`${String(channel.id)}:${String(userId)}`);
}

export function isLiveAttachment(cache: MockCache, attachment: Attachment): boolean {
  return attachmentOwnerState(cache, attachment) === 'alive';
}

export function canUserReadAttachment(cache: MockCache, userId: UserId, attachment: Attachment): boolean {
  if (attachment.status !== 'ready') return false;
  if (isPersonalAttachment(attachment)) return attachment.uploaderId === userId;
  const state = attachmentOwnerState(cache, attachment);
  if (state === 'gone') return false;
  if (attachment.uploaderId === userId) return true;
  if (state === 'draft') return false;
  return attachment.ownerKind !== 'message' || canSeeMessageChannel(cache, userId, attachment.ownerId as MessageId);
}

export function canReadAttachment(cache: MockCache, attachment: Attachment): boolean {
  return canUserReadAttachment(cache, cache.me, attachment);
}

function newerAttachment(a: Attachment, b: Attachment): number {
  return Number(b.createdAt) - Number(a.createdAt) || String(b.id).localeCompare(String(a.id));
}

export function findAttachmentByName(cache: MockCache, name: string, near: Attachment): Attachment | null {
  const wanted = name.toLowerCase();
  const candidates = [...cache.attachments.values()]
    .filter((candidate) => candidate.id !== near.id && candidate.name.toLowerCase() === wanted && canReadAttachment(cache, candidate))
    .sort(newerAttachment);
  const sameOwner = candidates.find((candidate) => candidate.ownerKind === near.ownerKind && candidate.ownerId === near.ownerId);
  return sameOwner ?? candidates[0] ?? null;
}

function byCreatedAtThenId(a: { createdAt: EpochMs; id: unknown }, b: { createdAt: EpochMs; id: unknown }): number {
  return Number(a.createdAt) - Number(b.createdAt) || String(a.id).localeCompare(String(b.id));
}

function isAdminOrMe(cache: MockCache, authorId: UserId): boolean {
  return authorId === cache.me || cache.users.get(cache.me)?.role === 'admin';
}

export function selectAnnotations(cache: MockCache, attachmentId: AttachmentId): Annotation[] {
  return [...cache.annotations.values()].filter((annotation) => annotation.attachmentId === attachmentId).sort(byCreatedAtThenId);
}

export function selectModelAnnotations(cache: MockCache, attachmentId: AttachmentId): ModelAnnotation[] {
  const points: ModelAnnotation[] = [];
  for (const annotation of selectAnnotations(cache, attachmentId)) {
    if (annotation.anchor.kind === 'model') {
      points.push({
        id: annotation.id,
        attachmentId: annotation.attachmentId,
        authorId: annotation.authorId,
        position: annotation.anchor.position,
        normal: annotation.anchor.normal,
        createdAt: annotation.createdAt,
      });
    }
  }
  return points;
}

export function selectAnnotationComments(cache: MockCache, annotationId: AnnotationId): AnnotationComment[] {
  return [...cache.annotationComments.values()].filter((comment) => comment.annotationId === annotationId).sort(byCreatedAtThenId);
}

export function canDeleteAnnotation(cache: MockCache, annotation: Pick<Annotation, 'authorId'>): boolean {
  return isAdminOrMe(cache, annotation.authorId);
}

export function canDeleteModelAnnotation(cache: MockCache, annotation: Pick<ModelAnnotation, 'authorId'>): boolean {
  return isAdminOrMe(cache, annotation.authorId);
}

export function canDeleteAnnotationComment(cache: MockCache, comment: AnnotationComment): boolean {
  return isAdminOrMe(cache, comment.authorId);
}

export function selectInboxForUser(
  cache: MockCache,
  userId: UserId,
  state?: InboxState,
): InboxItem[] {
  const items: InboxItem[] = [];
  for (const id of cache.index.inboxByUser.get(userId) ?? []) {
    const item = cache.inbox.get(id);
    if (item !== undefined && (state === undefined || item.state === state)) {
      items.push(item);
    }
  }
  items.sort((a, b) => Number(b.createdAt) - Number(a.createdAt) || b.seq - a.seq);
  return items;
}

export function selectInboxItem(cache: MockCache, id: InboxItemId): InboxItem | undefined {
  return cache.inbox.get(id);
}

export function selectUnreadInboxCount(cache: MockCache, userId: UserId): number {
  let count = 0;
  const now = Number(cache.nowMs());
  for (const id of cache.index.inboxByUser.get(userId) ?? []) {
    const item = cache.inbox.get(id);
    if (item === undefined || item.state !== 'unread') {
      continue;
    }
    if (item.snoozedUntilAt !== null && Number(item.snoozedUntilAt) > now) {
      continue;
    }
    count += 1;
  }
  return count;
}

export function selectNow(cache: MockCache): EpochMs {
  return cache.nowMs();
}

export function selectCall(cache: MockCache, id: CallId): Call | undefined {
  return cache.calls.get(id);
}

export function selectChannelCall(cache: MockCache, channelId: ChannelId): Call | undefined {
  for (const call of cache.calls.values()) {
    if (call.channelId === channelId && call.endedAt === null) return call;
  }
  return undefined;
}

export function selectMyCall(cache: MockCache): Call | undefined {
  for (const call of cache.calls.values()) {
    if (call.endedAt !== null) continue;
    if (call.participants.some((p) => p.userId === cache.me)) return call;
  }
  return undefined;
}

export function selectCallByRootMessage(cache: MockCache, rootMessageId: MessageId): Call | undefined {
  for (const call of cache.calls.values()) {
    if (call.rootMessageId === rootMessageId) return call;
  }
  return undefined;
}

export { selectMyThreads } from './my-threads';
export type { MyThread } from './my-threads';
